import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export interface TabConfig {
  index: string;
  route: string;
  label: string;
}

export type TabMapping = Record<string, TabConfig>;

/**
 * Hook to sync active tab with URL hash/query parameters
 * Handles bidirectional sync: URL changes update tab, tab changes update URL
 */
export const useTabSync = (tabMapping: TabMapping) => {
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Parses URL to extract tab index
   */
  const getTabFromUrl = (): number => {
    const hash = location.hash.substring(1);
    const tabEntry = Object.values(tabMapping).find((t) => t.route === hash);
    const queryTab = new URLSearchParams(location.search).get("tab");

    if (queryTab !== null) {
      const tabNumber = Number(queryTab);
      if (!isNaN(tabNumber) && tabNumber >= 0 && tabNumber <= 3) {
        return tabNumber;
      }
    } else if (tabEntry) {
      return Number(tabEntry.index);
    }

    return 0; // Default to first tab
  };

  const [activeTab, setActiveTab] = useState<number>(getTabFromUrl);

  // Sync URL changes to activeTab
  useEffect(() => {
    const urlTab = getTabFromUrl();
    if (urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, location.search]);

  // Update URL when active tab changes
  useEffect(() => {
    const tab = Object.values(tabMapping).find(
      (t) => t.index === activeTab.toString()
    );
    if (tab) {
      navigate(
        { pathname: location.pathname, hash: tab.route },
        { replace: true }
      );
      localStorage.setItem("lastActiveTab", activeTab.toString());
    }
  }, [activeTab, navigate, location.pathname, tabMapping]);

  return { activeTab, setActiveTab };
};
